import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import PropTypes from 'prop-types';

import Flex from '@utilities/components/Layout/components/Flex';
import { HelpV2Icon, IconButtonV2, toast } from '@utilities';

import {
  createChatSession,
  deleteProject,
  focusNarrativeFlow,
  hydrateDynamicNarrativeFlowState,
  readFlowState,
  readNarrativeDraft,
  readProject,
  readProjectFlows,
  readTokenUsage,
  updateFlowState,
  updateFlowStateTopicIds,
  updateNarrativeDraft,
} from '@api/narrative-projects';
import {
  readFlow,
  readNarrativeTopics,
  readNarrativeTopicsTags,
  softDeleteFlowTopic,
} from '@api/narratives';
import Narratives from '../Narratives';
import ConfirmActionModal from './components/ConfirmActionModal';
import NarrativesCanvas from './components/NarrativesCanvas';
import NarrativesChatLandingPage from './components/NarrativesChatLandingPage';
import NarrativesChatWindow from './components/NarrativesChatWindow';
import NexaCapacityTracker from './components/NexaCapacityTracker';
import NarrativesNavigation from './components/NarrativesNavigation';
import NarrativesTemplatesWindow from './components/NarrativesTemplatesWindow';
import NarrativesProjectHistory from './components/NarrativesProjectHistory';
import { buildDraftNarrative } from './components/NarrativesProjectHistory/utilities/helpers';
import NarrativesTopicsWindow from './components/NarrativesTopicsWindow';

import { HELP_CENTER_BASE_URL } from '../Narratives/utilities/constants';
import { DEFAULT_DRAFT_NARRATIVE_NAME, PAGES, WORKSPACE_PAGES } from './utilities/constants';
import { DEFAULT_PROJECT_NAME } from './components/NarrativesCanvas/utilities/constants';
import {
  areSameOrderedIds,
  buildInitialSessionState,
  buildTopicConfiguration,
  buildTopicConfigurationLookup,
  checkIsTopicCardCountMax,
  findFirstAvailableVariantSelection,
  getFlowTopicIds,
  getFlowTopicsSignature,
  getRunTopicComponentId,
  getTemplateTopicIds,
  mergeDraftIntoFlowState,
  mergeTopicIds,
  trackNarrativeTemplateUsed,
  trackTopicCardAddRemove,
} from './utilities/helpers';
import useDisposableProjectExit from './utilities/useDisposableProjectExit';

import styles from './_index.module.scss';

const NarrativesChat = ({ hasChatAccess }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const chatSessionIdFromUrl = searchParams.get('sessionId');
  const hasActiveProjectContext = Boolean(chatSessionIdFromUrl);
  const initialFlowIdFromNavigation = location.state?.initialFlowId ?? null;
  const requestedInitialFlowId = searchParams.get('narrativeId') ?? initialFlowIdFromNavigation;
  const narrativeIdFromUrl = searchParams.get('narrativeId');
  const defaultLandingPage = hasChatAccess ? PAGES.LANDING : PAGES.TEMPLATES;
  const getInitialActivePage = () => {
    if (hasChatAccess) {
      return chatSessionIdFromUrl ? PAGES.CHAT : PAGES.LANDING;
    }
    return chatSessionIdFromUrl ? PAGES.PAST_PROJECTS : PAGES.TEMPLATES;
  };
  const [activePage, setActivePage] = useState(getInitialActivePage);
  const [flowId, setFlowId] = useState(null);
  const [draftFlowState, setDraftFlowState] = useState(null);
  const [initialInput, setInitialInput] = useState('');
  const [projectNamesBySession, setProjectNamesBySession] = useState({});
  const [emptyChatHistoryBySession, setEmptyChatHistoryBySession] = useState({});
  const [deletedNarrativeIdsBySession, setDeletedNarrativeIdsBySession] = useState({});
  const [draftRefreshKeysBySession, setDraftRefreshKeysBySession] = useState({});
  const [projectNarrativesBySession, setProjectNarrativesBySession] = useState({});
  const [favoriteStatusBySession, setFavoriteStatusBySession] = useState({});
  const [narrativeNamesById, setNarrativeNamesById] = useState({});
  const [optimisticNarrativesBySession, setOptimisticNarrativesBySession] = useState({});
  const [runFlowTopicIds, setRunFlowTopicIds] = useState(null);
  const [sessionState, setSessionState] = useState(() => ({
    ...buildInitialSessionState(),
    chatSessionId: chatSessionIdFromUrl || null,
  }));
  const hasRequestedInitialFlowSelection =
    requestedInitialFlowId != null && chatSessionIdFromUrl === sessionState.chatSessionId;
  const [selectedTopicCards, setSelectedTopicCards] = useState([]);
  const [pendingProjectSelection, setPendingProjectSelection] = useState(null);
  const [activeTopicConfiguration, setActiveTopicConfiguration] = useState(null);
  const [narrativeTopicGroups, setNarrativeTopicGroups] = useState(null);
  const [visibleTopicGroups, setVisibleTopicGroups] = useState(null);
  const [chatSnapshot, setChatSnapshot] = useState(null);
  const [pendingChatResponse, setPendingChatResponse] = useState(null);
  const [tokenUsage, setTokenUsage] = useState(null);

  const refreshProjectDraft = useCallback((chatSessionId) => {
    if (!chatSessionId) return;

    setDraftRefreshKeysBySession((previousRefreshKeys) => ({
      ...previousRefreshKeys,
      [chatSessionId]: (previousRefreshKeys?.[chatSessionId] || 0) + 1,
    }));
  }, []);

  const loadTokenUsage = useCallback(async ({ signal } = {}) => {
    try {
      const nextTokenUsage = await readTokenUsage({ signal });
      setTokenUsage(nextTokenUsage);
    } catch (error) {
      if (error?.name === 'AbortError' || error?.name === 'CanceledError') return;
      console.error('Failed to read Nexa token usage', error);
    }
  }, []);

  useEffect(() => {
    if (!hasChatAccess) return undefined;

    const abortController = new AbortController();
    loadTokenUsage({ signal: abortController.signal });

    return () => abortController.abort();
  }, [hasChatAccess, loadTokenUsage]);
  const [runTopicPendingDeletion, setRunTopicPendingDeletion] = useState(null);
  const [isDeletingRunTopic, setIsDeletingRunTopic] = useState(false);
  const [runFlowRefreshKey, setRunFlowRefreshKey] = useState(0);
  const [isLeftPaneCollapsed, setIsLeftPaneCollapsed] = useState(
    chatSessionIdFromUrl ? true : false
  );
  const [workspaceTemplateContext, setWorkspaceTemplateContext] = useState({
    activeTab: 'numerator',
    selectedTemplateId: null,
  });
  const [customTemplatesRefreshKey, setCustomTemplatesRefreshKey] = useState(0);
  const [pendingInsertTopicCard, setPendingInsertTopicCard] = useState(null);
  const [isInsertingTopicCard, setIsInsertingTopicCard] = useState(false);
  const [pendingTemplateSelection, setPendingTemplateSelection] = useState(null);
  const [isTemplateDirty, setIsTemplateDirty] = useState(true);
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [hasTagLoadError, setHasTagLoadError] = useState(false);
  const [hasLoadedTopicTags, setHasLoadedTopicTags] = useState(false);
  const [lastDeletedProjectSessionId, setLastDeletedProjectSessionId] = useState(null);
  const [isCreatingEmptyProject, setIsCreatingEmptyProject] = useState(false);
  const [isValidatingUrlProject, setIsValidatingUrlProject] = useState(
    Boolean(chatSessionIdFromUrl)
  );
  const [hasCompletedInitialUrlProjectValidation, setHasCompletedInitialUrlProjectValidation] =
    useState(!chatSessionIdFromUrl);

  const [isResolvingInitialCanvasContent, setIsResolvingInitialCanvasContent] = useState(
    Boolean(chatSessionIdFromUrl)
  );

  const initialRequestedFlowIdRef = useRef(requestedInitialFlowId);

  const initialNarrativeIdFromUrlRef = useRef(narrativeIdFromUrl);
  const initialFlowIdFromNavigationRef = useRef(initialFlowIdFromNavigation);
  const [projectDraftNarrative, setProjectDraftNarrative] = useState(null);
  const [projectDraftLookup, setProjectDraftLookup] = useState({
    chatSessionId: null,
    status: 'idle',
  });
  const creatingChatSessionPromiseRef = useRef(null);
  const breakawaySyncRequestIdRef = useRef(0);
  const breakawayAddRequestIdRef = useRef(0);
  const runFlowPromptAnswersRef = useRef({});
  const narrativeTopicGroupsRequestRef = useRef(null);
  const narrativeTopicGroupsRequestIdRef = useRef(0);
  const narrativeTopicTagsRequestRef = useRef(null);
  const isProjectSelectionInProgressRef = useRef(false);
  const previousRequestedFlowSelectionRef = useRef(null);
  const tagFilterRequestIdRef = useRef(0);
  const templateApplyRequestIdRef = useRef(0);
  const previousChatSessionIdFromUrlRef = useRef(chatSessionIdFromUrl);
  const previousDraftFlowStateRef = useRef(draftFlowState);
  const projectDraftRequestIdRef = useRef(0);
  const projectDraftSessionIdRef = useRef(null);
  const activeProjectIdRef = useRef(null);
  const lastActiveDraftFlowStateRef = useRef(null);
  const [activeProjectIdTick, setActiveProjectIdTick] = useState(0);
  const pendingInsertTopicCardResolverRef = useRef(null);
  const isClearingChatSessionRef = useRef(false);

  const activeDraftRefreshKey = draftRefreshKeysBySession?.[sessionState.chatSessionId] || 0;
  const isProjectDraftLookupPending =
    projectDraftLookup.chatSessionId !== sessionState.chatSessionId ||
    projectDraftLookup.status === 'loading';

  useEffect(() => {
    const chatSessionId = sessionState.chatSessionId;
    if (!chatSessionId) {
      projectDraftSessionIdRef.current = null;
      activeProjectIdRef.current = null;
      lastActiveDraftFlowStateRef.current = null;
      setProjectDraftNarrative(null);
      setProjectDraftLookup({ chatSessionId: null, status: 'idle' });
      return undefined;
    }

    const controller = new AbortController();
    const requestId = ++projectDraftRequestIdRef.current;
    const didProjectSessionChange = projectDraftSessionIdRef.current !== chatSessionId;
    projectDraftSessionIdRef.current = chatSessionId;

    if (didProjectSessionChange) {
      activeProjectIdRef.current = null;
      lastActiveDraftFlowStateRef.current = null;
      setProjectDraftNarrative(null);
    }
    if (projectDraftLookup.chatSessionId === chatSessionId) {
      setProjectDraftLookup({ chatSessionId, status: 'loading' });
    }

    readProject({ chatSessionId, signal: controller.signal })
      .then((project) => {
        if (requestId === projectDraftRequestIdRef.current) {
          activeProjectIdRef.current = project?.id ?? null;
          setActiveProjectIdTick((tick) => tick + 1);
        }
        if (!project?.id) return { draft: null, projectId: null };
        return readNarrativeDraft({ projectId: project.id, signal: controller.signal }).then(
          (draft) => ({ draft, projectId: project.id })
        );
      })
      .then((result) => {
        if (!result || requestId !== projectDraftRequestIdRef.current) return;
        const persistedDraft = buildDraftNarrative({
          draft: result.draft,
          projectId: result.projectId,
        });
        setProjectDraftNarrative(persistedDraft);
        setProjectDraftLookup({ chatSessionId, status: 'loaded' });
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || error?.name === 'CanceledError') return;
        if (requestId !== projectDraftRequestIdRef.current) return;
        setProjectDraftLookup({ chatSessionId, status: 'error' });
        console.error('Error reading the active project draft:', error);
      });

    return () => {
      projectDraftRequestIdRef.current += 1;
      controller.abort();
    };
  }, [activeDraftRefreshKey, sessionState.chatSessionId]);

  useEffect(() => {
    if (!sessionState.chatSessionId || !isProjectDraftLookupPending) return;

    const isBareDraftMode = flowId == null && draftFlowState?.focusFlowId == null;
    if (isBareDraftMode && draftFlowState) {
      lastActiveDraftFlowStateRef.current = draftFlowState;
    }

    const projectId = activeProjectIdRef.current;
    if (!projectId) return;

    const computedDraft = buildDraftNarrative({
      draft: lastActiveDraftFlowStateRef.current,
      projectId,
    });
    if (computedDraft) {
      setProjectDraftNarrative(computedDraft);
    }
  }, [
    sessionState.chatSessionId,
    flowId,
    draftFlowState,
    activeProjectIdTick,
    isProjectDraftLookupPending,
  ]);

  const setSynchronizedDraftFlowState = useCallback((nextFlowState) => {
    const synchronizedFlowState =
      typeof nextFlowState === 'function'
        ? nextFlowState(previousDraftFlowStateRef.current)
        : nextFlowState;

    previousDraftFlowStateRef.current = synchronizedFlowState;
    setDraftFlowState(synchronizedFlowState);
  }, []);
  const isLanding = activePage === PAGES.LANDING;
  const isTemplates = activePage === PAGES.TEMPLATES;
  const isChat = activePage === PAGES.CHAT;
  const isTopics = activePage === PAGES.TOPICS;
  const isWorkspaceTemplates = activePage === PAGES.WORKSPACE_TEMPLATES;
  const isPastProjects = activePage === PAGES.PAST_PROJECTS;
  const shouldDisableBodyScroll = !isLanding && !isTemplates;
  const shouldShowWorkspace = isChat || isTopics || isPastProjects || isWorkspaceTemplates;
  const shouldRenderNavigation =
    !narrativeIdFromUrl || !isValidatingUrlProject || hasCompletedInitialUrlProjectValidation;
  const shouldRenderWorkspace =
    shouldShowWorkspace && (!isValidatingUrlProject || hasCompletedInitialUrlProjectValidation);
  const hasSelectedTopics = selectedTopicCards.length > 0;
  const hasWorkspaceContent = Boolean(flowId) || Boolean(draftFlowState) || hasSelectedTopics;
  const shouldAllowCanvasHydrationOnMount =
    !hasWorkspaceContent && !hasRequestedInitialFlowSelection;
  const websocketFlows = useSelector((state) => state?.websocket?.flows);
  const activeRunFlow = useMemo(() => {
    if (!flowId || !Array.isArray(websocketFlows)) return null;
    return websocketFlows.find((flow) => Number(flow?.id) === Number(flowId)) || null;
  }, [flowId, websocketFlows]);

  const flowStateTopicIds = useMemo(() => {
    if (!Array.isArray(draftFlowState?.topics)) return [];
    return draftFlowState.topics.filter((topicId) => topicId != null);
  }, [draftFlowState]);

  const selectedTopicIds = useMemo(() => {
    const manualIds = selectedTopicCards.map((topic) => topic.id).filter((id) => id != null);
    if (flowId) {
      return flowStateTopicIds.length ? flowStateTopicIds : manualIds;
    }
    if (!flowStateTopicIds.length) {
      return manualIds;
    }
    return Array.from(new Set([...flowStateTopicIds, ...manualIds]));
  }, [flowId, flowStateTopicIds, selectedTopicCards]);
  const hasCanvasCards = useMemo(() => {
    if (flowId) return true;
    return selectedTopicIds.length > 0;
  }, [flowId, selectedTopicIds]);
  const topicIdsForLibrary = useMemo(() => {
    if (flowId) {
      if (Array.isArray(runFlowTopicIds) && runFlowTopicIds.length > 0) {
        return runFlowTopicIds;
      }

      const activeRunTopicIds = Array.isArray(activeRunFlow?.topics)
        ? activeRunFlow.topics
            .map((topic) => getRunTopicComponentId(topic))
            .filter((componentId) => componentId != null)
        : [];

      if (activeRunTopicIds.length > 0) {
        return activeRunTopicIds;
      }
    }

    return selectedTopicIds;
  }, [activeRunFlow?.topics, flowId, runFlowTopicIds, selectedTopicIds]);
  const topicConfigurationLookup = useMemo(
    () => buildTopicConfigurationLookup(narrativeTopicGroups),
    [narrativeTopicGroups]
  );

  const normalizedSelectedTags = useMemo(
    () => Array.from(new Set(selectedTags.filter(Boolean))).sort(),
    [selectedTags]
  );
  const selectedTagsKey = useMemo(() => normalizedSelectedTags.join(','), [normalizedSelectedTags]);

  const shouldLoadNarrativeTopicGroups = isTopics || hasCanvasCards || isChat;
  const activeChatSnapshot = useMemo(() => {
    if (!chatSnapshot) return null;
    if (chatSnapshot.chatSessionId !== sessionState.chatSessionId) return null;
    return chatSnapshot;
  }, [chatSnapshot, sessionState.chatSessionId]);
  const replaceCanvasQueryParams = useCallback(
    (options = {}) => {
      const hasNarrativeId = Object.prototype.hasOwnProperty.call(options, 'narrativeId');
      const { chatSessionId } = options;
      const narrativeId = hasNarrativeId ? (options.narrativeId ?? null) : narrativeIdFromUrl;
      const params = new URLSearchParams(searchParams);
      if (chatSessionId) {
        params.set('sessionId', chatSessionId);
      } else {
        params.delete('sessionId');
        params.delete('narrativeId');
      }

      if (chatSessionId && hasNarrativeId) {
        if (narrativeId) {
          params.set('narrativeId', narrativeId);
        } else {
          params.delete('narrativeId');
        }
      }
      setSearchParams(params, { preventScrollReset: true, replace: true });
    },
    [narrativeIdFromUrl, searchParams, setSearchParams]
  );

  const removeSessionScopedState = useCallback((chatSessionId) => {
    if (!chatSessionId) return;

    const removeSessionEntry = (setState) => {
      setState((previousState) => {
        if (!previousState || !Object.prototype.hasOwnProperty.call(previousState, chatSessionId)) {
          return previousState;
        }

        const nextState = { ...previousState };
        delete nextState[chatSessionId];
        return nextState;
      });
    };

    removeSessionEntry(setProjectNamesBySession);
    removeSessionEntry(setEmptyChatHistoryBySession);
    removeSessionEntry(setDeletedNarrativeIdsBySession);
    removeSessionEntry(setProjectNarrativesBySession);
    removeSessionEntry(setFavoriteStatusBySession);
    removeSessionEntry(setOptimisticNarrativesBySession);
    setPendingChatResponse((previousPendingResponse) =>
      previousPendingResponse?.chatSessionId === chatSessionId ? null : previousPendingResponse
    );
  }, []);

  const deleteDisposableProject = useCallback(
    async (chatSessionId) => {
      if (!chatSessionId) return;

      await deleteProject({ projectId: chatSessionId });
      removeSessionScopedState(chatSessionId);
      setLastDeletedProjectSessionId(chatSessionId);
    },
    [removeSessionScopedState]
  );
  const {
    cancelExit: handleDisposableProjectExitCancel,
    confirmExit: handleDisposableProjectExitConfirm,
    isDeleting: isDeletingDisposableProject,
    isExitConfirmationOpen: isDisposableProjectExitConfirmationOpen,
    requestExit: requestDisposableProjectExit,
    requestProjectOpen: handleBeforeProjectOpen,
  } = useDisposableProjectExit({
    activeChatSnapshot,
    chatSessionId: sessionState.chatSessionId,
    creatingChatSessionPromiseRef,
    deletedNarrativeIds: deletedNarrativeIdsBySession?.[sessionState.chatSessionId],
    emptyChatHistory: Boolean(emptyChatHistoryBySession?.[sessionState.chatSessionId]),
    flowId,
    isCreatingProject: isCreatingEmptyProject,
    onDeleteProject: deleteDisposableProject,
    optimisticNarratives: optimisticNarrativesBySession?.[sessionState.chatSessionId],
    persistedNarratives: projectNarrativesBySession?.[sessionState.chatSessionId],
    projectName: projectNamesBySession?.[sessionState.chatSessionId],
  });

  const reconcileDraftFlowState = useCallback(async ({ chatSessionId, fallbackFlowState }) => {
    if (!chatSessionId) return fallbackFlowState;

    try {
      const latestFlowStateResponse = await readFlowState({ chatSessionId });
      const reconciledFlowState = latestFlowStateResponse?.flowState;
      if (!reconciledFlowState) return fallbackFlowState;
      // updateFlowStateTopicIds only persists topics, so the re-read can still carry a
      // stale run-linked focusFlowId. Keep the draft detached so it stays in Past Projects.
      return { ...reconciledFlowState, focusFlowId: null };
    } catch (error) {
      if (error?.name !== 'AbortError' && error?.name !== 'CanceledError') {
        console.error('Failed to re-read draft flow state after sync', error);
      }
      return fallbackFlowState;
    }
  }, []);

  const resetSessionState = useCallback(() => {
    isClearingChatSessionRef.current = true;
    setSessionState(buildInitialSessionState());
    setChatSnapshot(null);
    setPendingChatResponse(null);
    setFlowId(null);
    setSynchronizedDraftFlowState(null);
    setRunFlowTopicIds(null);
    setIsDeletingRunTopic(false);
    setIsLeftPaneCollapsed(false);
    setRunFlowRefreshKey(0);
    setRunTopicPendingDeletion(null);
    setActiveTopicConfiguration(null);
    setSelectedTopicCards([]);
    setWorkspaceTemplateContext({ activeTab: 'numerator', selectedTemplateId: null });
    setCustomTemplatesRefreshKey(0);
    if (pendingInsertTopicCardResolverRef.current) {
      pendingInsertTopicCardResolverRef.current(false);
      pendingInsertTopicCardResolverRef.current = null;
    }
    setPendingInsertTopicCard(null);
    setPendingTemplateSelection(null);
    setNarrativeNamesById({});
    setProjectNamesBySession({});
    setEmptyChatHistoryBySession({});
    setProjectNarrativesBySession({});
    setFavoriteStatusBySession({});
    setProjectDraftNarrative(null);
    setIsCreatingEmptyProject(false);
    setIsTemplateDirty(true);
    replaceCanvasQueryParams({ chatSessionId: null });
    setSelectedTags([]);
    setNarrativeTopicGroups(null);
    setVisibleTopicGroups(null);
    setAvailableTags([]);
    setHasTagLoadError(false);
    setHasLoadedTopicTags(false);
    tagFilterRequestIdRef.current = 0;
  }, [replaceCanvasQueryParams]);

  const clearActiveSessionSelection = useCallback(
    ({ chatSessionId, shouldRemoveSessionScopedState = false } = {}) => {
      isClearingChatSessionRef.current = true;
      if (shouldRemoveSessionScopedState && chatSessionId) {
        removeSessionScopedState(chatSessionId);
      }
      setSessionState(buildInitialSessionState());
      setChatSnapshot(null);
      setPendingChatResponse(null);
      setFlowId(null);
      setSynchronizedDraftFlowState(null);
      setRunFlowTopicIds(null);
      setIsDeletingRunTopic(false);
      setIsLeftPaneCollapsed(false);
      setRunFlowRefreshKey(0);
      setRunTopicPendingDeletion(null);
      setActiveTopicConfiguration(null);
      setSelectedTopicCards([]);
      if (pendingInsertTopicCardResolverRef.current) {
        pendingInsertTopicCardResolverRef.current(false);
        pendingInsertTopicCardResolverRef.current = null;
      }
      setPendingInsertTopicCard(null);
      setPendingTemplateSelection(null);
      replaceCanvasQueryParams({ chatSessionId: null });
    },
    [removeSessionScopedState, replaceCanvasQueryParams, setSynchronizedDraftFlowState]
  );

  const resetCanvasSelectionState = useCallback(
    ({ shouldResetTopicFilters = false } = {}) => {
      setSelectedTopicCards([]);
      setSynchronizedDraftFlowState(null);
      setActiveTopicConfiguration(null);
      setRunFlowTopicIds(null);
      runFlowPromptAnswersRef.current = {};

      if (shouldResetTopicFilters) {
        setSelectedTags([]);
        setVisibleTopicGroups(narrativeTopicGroups);
        tagFilterRequestIdRef.current += 1;
      }
    },
    [narrativeTopicGroups]
  );

  const restoreRunSelection = useCallback(
    (nextFlowId, { chatSessionId } = {}) => {
      setFlowId(nextFlowId);

      const sessionId = chatSessionId || sessionState.chatSessionId;
      if (sessionId) {
        replaceCanvasQueryParams({
          chatSessionId: sessionId,
          narrativeId: nextFlowId ?? null,
        });
      }
    },
    [replaceCanvasQueryParams, sessionState.chatSessionId]
  );

  const clearRunSelection = useCallback(
    (options) => restoreRunSelection(null, options),
    [restoreRunSelection]
  );

  const clearFocusedRunFlow = useCallback(({ chatSessionId, previousFlowId }) => {
    if (!chatSessionId || previousFlowId == null) return Promise.resolve();

    return focusNarrativeFlow({ chatSessionId, flowId: null, inflateFocusFlow: false });
  }, []);

  const restoreFocusedRunFlow = useCallback(({ chatSessionId, previousFlowId }) => {
    if (!chatSessionId || previousFlowId == null) return Promise.resolve();

    return focusNarrativeFlow({
      chatSessionId,
      flowId: previousFlowId,
      inflateFocusFlow: true,
    }).catch((error) => {
      console.error('Failed to restore the focused run Narrative', error);
    });
  }, []);

  const handleChatWindowInitializingChange = useCallback((isChatWindowInitializing) => {
    if (isChatWindowInitializing) return;

    if (initialRequestedFlowIdRef.current) return;
    setIsResolvingInitialCanvasContent(false);
  }, []);

  const handleChatSessionReady = useCallback(
    (sessionId) => {
      if (!sessionId) return;
      isClearingChatSessionRef.current = false;
      if (sessionState.chatSessionId && sessionState.chatSessionId !== sessionId) {
        setActiveTopicConfiguration(null);
        setSynchronizedDraftFlowState(null);
        setFlowId(null);
        setRunFlowTopicIds(null);
        setSelectedTopicCards([]);
        runFlowPromptAnswersRef.current = {};
      }
      setSessionState((prev) => {
        if (prev.chatSessionId === sessionId) return prev;
        return { ...prev, chatSessionId: sessionId };
      });
      replaceCanvasQueryParams({
        chatSessionId: sessionId,
        ...(sessionState.chatSessionId !== sessionId ? { narrativeId: null } : {}),
      });
      setActivePage((prev) => (prev === PAGES.LANDING ? PAGES.CHAT : prev));
    },
    [replaceCanvasQueryParams, sessionState.chatSessionId]
  );

  const handleFlowSelected = useCallback(
    (nextFlowId, options = {}) => {
      if (nextFlowId != null && options.isOwner === false) {
        navigate(`/dashboard/narrative/${nextFlowId}`, { replace: true });
        return false;
      }

      setActiveTopicConfiguration(null);
      setFlowId(nextFlowId);
      setRunFlowTopicIds(null);
      runFlowPromptAnswersRef.current = {};

      const selectedSessionId = options.chatSessionId || sessionState.chatSessionId;
      const isHydrating =
        options.source === 'chat-hydration' || options.source === 'flow-state-hydration';
      if (selectedSessionId && (!isHydrating || !narrativeIdFromUrl)) {
        replaceCanvasQueryParams({
          chatSessionId: selectedSessionId,
          narrativeId: nextFlowId,
        });
      }

      return true;
    },
    [navigate, narrativeIdFromUrl, replaceCanvasQueryParams, sessionState.chatSessionId]
  );

  const handleDraftSelected = useCallback(
    (nextFlowState, { chatSessionId } = {}) => {
      setActiveTopicConfiguration(null);
      clearRunSelection({ chatSessionId });
      setRunFlowTopicIds(null);
      setSelectedTopicCards([]);
      setSynchronizedDraftFlowState(nextFlowState || null);
      setIsTemplateDirty(true);
    },
    [clearRunSelection]
  );

  const handleProjectDraftSelected = useCallback(
    async ({ chatSessionId, projectId }) => {
      if (!chatSessionId || !projectId || isProjectSelectionInProgressRef.current) return false;

      isProjectSelectionInProgressRef.current = true;
      setPendingProjectSelection({ chatSessionId, flowId: null });

      try {
        const draft = await readNarrativeDraft({ projectId });
        if (!draft) throw new Error('Draft not found.');

        await focusNarrativeFlow({
          chatSessionId,
          flowId: null,
          inflateFocusFlow: false,
        });
        await updateFlowState({
          chatSessionId,
          flowState: {
            editablePromptAnswers: draft.editablePromptAnswers || {},
            narrativeName: draft.narrativeName,
            topics: draft.topics,
          },
        });
        const { flowState } = await readFlowState({ chatSessionId });

        handleChatSessionReady(chatSessionId);

        handleDraftSelected(flowState, { chatSessionId });
        return true;
      } catch (error) {
        console.error('Failed to select project draft', error);
        toast.error('We could not open this Draft. Please try again.');
        return false;
      } finally {
        isProjectSelectionInProgressRef.current = false;

        setPendingProjectSelection(null);
      }
    },
    [handleChatSessionReady, handleDraftSelected]
  );

  const handleProjectNarrativeSelected = useCallback(
    async ({ chatSessionId, flowId: nextFlowId, signal }) => {
      if (
        !chatSessionId ||
        nextFlowId === null ||
        nextFlowId === undefined ||
        isProjectSelectionInProgressRef.current
      )
        return false;

      isProjectSelectionInProgressRef.current = true;
      setPendingProjectSelection({ chatSessionId, flowId: nextFlowId });

      try {
        await readFlow({ id: nextFlowId, ...(signal ? { signal } : {}) });
        if (signal?.aborted) return false;

        await focusNarrativeFlow({
          chatSessionId,
          flowId: nextFlowId,
          inflateFocusFlow: true,
          ...(signal ? { signal } : {}),
        });
        if (signal?.aborted) return false;

        const { flowState } = await readFlowState({
          chatSessionId,
          ...(signal ? { signal } : {}),
        });
        if (signal?.aborted) return false;

        handleChatSessionReady(chatSessionId);
        setSelectedTopicCards([]);
        setSynchronizedDraftFlowState(flowState);
        handleFlowSelected(nextFlowId, { chatSessionId });
        return true;
      } catch (error) {
        if (error?.name === 'AbortError' || error?.name === 'CanceledError') return false;
        console.error('Failed to select project narrative', error);
        toast.error('We could not open this Narrative. Please try again.');
        return false;
      } finally {
        isProjectSelectionInProgressRef.current = false;
        // On success this lands with the committed selection, so the highlight does not move;
        // on failure it reverts to whatever is still on the canvas.
        setPendingProjectSelection(null);
      }
    },
    [handleChatSessionReady, handleFlowSelected, setSynchronizedDraftFlowState]
  );

  const loadNarrativeTopicGroups = useCallback(
    async ({ showError = true } = {}) => {
      if (Array.isArray(narrativeTopicGroups)) {
        return narrativeTopicGroups;
      }

      if (narrativeTopicGroupsRequestRef.current) {
        return narrativeTopicGroupsRequestRef.current;
      }

      const requestId = ++narrativeTopicGroupsRequestIdRef.current;
      const request = (async () => {
        try {
          const topics = await readNarrativeTopics({ signal: undefined });
          const normalizedTopics = Array.isArray(topics) ? topics : [];
          if (narrativeTopicGroupsRequestIdRef.current !== requestId) return normalizedTopics;
          setNarrativeTopicGroups(normalizedTopics);
          return normalizedTopics;
        } catch (error) {
          if (narrativeTopicGroupsRequestIdRef.current !== requestId) return [];
          console.error('Error fetching narrative topics:', error);
          if (showError) {
            setNarrativeTopicGroups([]);
            toast.error('Error loading topics. Please refresh the page or try again.');
          }
          return [];
        } finally {
          if (narrativeTopicGroupsRequestIdRef.current === requestId) {
            narrativeTopicGroupsRequestRef.current = null;
          }
        }
      })();

      narrativeTopicGroupsRequestRef.current = request;
      return request;
    },
    [narrativeTopicGroups]
  );

  const handleFlowStateChange = useCallback(
    (nextFlowState, options = {}) => {
      if (!options.shouldBreakAwayFromRun && nextFlowState == null) {
        setSynchronizedDraftFlowState(null);
        setIsTemplateDirty(true);
        return;
      }

      if (!options.shouldBreakAwayFromRun || !nextFlowState) {
        const previousFlowState = previousDraftFlowStateRef.current;
        const normalizedFlowState = {
          ...(previousFlowState || {}),
          ...(nextFlowState || {}),
          narrativeName:
            nextFlowState?.narrativeName ||
            previousFlowState?.narrativeName ||
            DEFAULT_DRAFT_NARRATIVE_NAME,
        };
        const didComponentsChange =
          getFlowTopicsSignature(previousFlowState) !== getFlowTopicsSignature(normalizedFlowState);
        const didNarrativeNameChange =
          (previousFlowState?.narrativeName || DEFAULT_DRAFT_NARRATIVE_NAME) !==
          (normalizedFlowState?.narrativeName || DEFAULT_DRAFT_NARRATIVE_NAME);

        if (didComponentsChange || didNarrativeNameChange) {
          setIsTemplateDirty(true);
        }

        const isSourceOfChangeChat = options?.sourceOfChange === 'chat';
        if (isSourceOfChangeChat) {
          const prevFlowStateTopics = getFlowTopicIds(previousFlowState);
          const nextFlowStateTopics = getFlowTopicIds(normalizedFlowState);

          const addedTopicIds = nextFlowStateTopics?.filter(
            (topic) => !prevFlowStateTopics?.includes(topic)
          );

          const removedTopicIds = prevFlowStateTopics?.filter(
            (topic) => !nextFlowStateTopics?.includes(topic)
          );
          if (addedTopicIds?.length || removedTopicIds?.length) {
            const currentSessionId = sessionState.chatSessionId;
            const trackTopicChanges = async () => {
              const groups = Array.isArray(narrativeTopicGroups)
                ? narrativeTopicGroups
                : await loadNarrativeTopicGroups({ showError: false });
              const nextTopicConfigurationLookup = buildTopicConfigurationLookup(groups);

              addedTopicIds?.forEach((topicId) => {
                const topicName = nextTopicConfigurationLookup[topicId]?.name ?? 'n/a';
                trackTopicCardAddRemove({
                  action: 'add',
                  chatSessionId: currentSessionId,
                  environmentState: 'draft',
                  sourceOfChange: 'chat',
                  topicId,
                  topicName,
                });
              });
              removedTopicIds?.forEach((topicId) => {
                const topicName = nextTopicConfigurationLookup[topicId]?.name ?? 'n/a';
                trackTopicCardAddRemove({
                  action: 'remove',
                  chatSessionId: currentSessionId,
                  environmentState: 'draft',
                  sourceOfChange: 'chat',
                  topicId,
                  topicName,
                });
              });
            };

            void trackTopicChanges();
          }
        }

        const hadNoDraftTopics = getFlowTopicIds(previousFlowState).length === 0;
        const hasDraftTopicsNow = getFlowTopicIds(normalizedFlowState).length > 0;
        if (
          isSourceOfChangeChat &&
          normalizedFlowState?.focusFlowId == null &&
          hadNoDraftTopics &&
          hasDraftTopicsNow
        ) {
          refreshProjectDraft(sessionState.chatSessionId);
        }

        setSynchronizedDraftFlowState(normalizedFlowState);
        return;
      }

      let nextDraftFlowState = nextFlowState;
      const fallbackRunTopicIds = Array.isArray(activeRunFlow?.topics)
        ? activeRunFlow.topics
            .filter((topic) => !topic?.isHidden)
            .map((topic) => getRunTopicComponentId(topic))
            .filter((componentId) => componentId != null)
        : null;
      const baseRunTopicIds = Array.isArray(runFlowTopicIds)
        ? runFlowTopicIds
        : fallbackRunTopicIds;
      if (Array.isArray(baseRunTopicIds) && Array.isArray(nextFlowState.topics)) {
        const runTopicIdSet = new Set(baseRunTopicIds.map(String));
        const updatedTopicIdSet = new Set(getFlowTopicIds(nextFlowState));
        const removedTopicIds = baseRunTopicIds
          .map(String)
          .filter((topicId) => !updatedTopicIdSet.has(topicId));
        if (removedTopicIds.length) {
          removedTopicIds.forEach((topicId) => {
            const topicName = topicConfigurationLookup[topicId]?.name ?? 'n/a';
            trackTopicCardAddRemove({
              action: 'remove',
              chatSessionId: sessionState.chatSessionId,
              environmentState: 'completed_narrative',
              flowId,
              sourceOfChange: 'chat',
              topicId,
              topicName,
            });
          });
        }
        const addedTopicIds = nextFlowState.topics.filter(
          (topicId) => !runTopicIdSet.has(String(topicId))
        );
        const nextTopicIds = mergeTopicIds(baseRunTopicIds, addedTopicIds);

        nextDraftFlowState = {
          ...nextFlowState,
          focusFlowId: null,
          topics: nextTopicIds,
        };

        const focusCleared = clearFocusedRunFlow({
          chatSessionId: sessionState.chatSessionId,
          previousFlowId: flowId,
        });

        if (sessionState.chatSessionId && !areSameOrderedIds(nextFlowState.topics, nextTopicIds)) {
          const syncRequestId = ++breakawaySyncRequestIdRef.current;
          const expectedTopicsSignature = JSON.stringify(nextTopicIds.map(String));
          focusCleared
            .then(() => readProject({ chatSessionId: sessionState.chatSessionId }))
            .then((project) => {
              if (!project?.id) throw new Error('Project ID is required to update a Draft.');

              return updateNarrativeDraft({
                draft: nextDraftFlowState,
                projectId: project.id,
              });
            })
            .then((draft) =>
              hydrateDynamicNarrativeFlowState({
                flowState: mergeDraftIntoFlowState({ draft, flowState: nextDraftFlowState }),
              })
            )
            .then((updatedFlowState) => {
              if (syncRequestId !== breakawaySyncRequestIdRef.current) return;
              refreshProjectDraft(sessionState.chatSessionId);
              setSynchronizedDraftFlowState((currentFlowState) => {
                const currentTopics = Array.isArray(currentFlowState?.topics)
                  ? currentFlowState.topics
                  : [];
                if (expectedTopicsSignature !== JSON.stringify(currentTopics.map(String))) {
                  return currentFlowState;
                }
                return updatedFlowState || nextDraftFlowState;
              });
            })
            .catch((error) => {
              console.error('Failed to save draft flow state after chat breakaway', error);
              toast.error('We could not sync the draft topic cards. Please refresh and try again.');
            });
        } else {
          focusCleared.catch((error) => {
            console.error('Failed to clear the focused run Narrative after chat breakaway', error);
          });
        }
      }
      setSynchronizedDraftFlowState((previousFlowState) => ({
        ...(previousFlowState || {}),
        ...(nextDraftFlowState || {}),
        focusFlowId: null,
        narrativeName:
          nextDraftFlowState?.narrativeName ||
          previousFlowState?.narrativeName ||
          DEFAULT_DRAFT_NARRATIVE_NAME,
      }));
      setActiveTopicConfiguration(null);
      // The session is already settled on this path, so the sessionState fallback is safe.
      clearRunSelection();
      setSelectedTopicCards([]);
      setIsTemplateDirty(true);
    },
    [
      activeRunFlow,
      clearFocusedRunFlow,
      clearRunSelection,
      flowId,
      loadNarrativeTopicGroups,
      narrativeTopicGroups,
      reconcileDraftFlowState,
      refreshProjectDraft,
      runFlowTopicIds,
      sessionState.chatSessionId,
      topicConfigurationLookup,
    ]
  );

  const ensureChatSession = useCallback(async () => {
    if (sessionState.chatSessionId) return sessionState.chatSessionId;
    if (creatingChatSessionPromiseRef.current) return creatingChatSessionPromiseRef.current;

    setIsCreatingEmptyProject(true);
    const createPromise = (async () => {
      try {
        const sessionId = await createChatSession();
        if (!sessionId) throw new Error('Chat service did not return a session identifier.');
        setProjectNamesBySession((previousNames) => ({
          ...previousNames,
          [sessionId]: DEFAULT_PROJECT_NAME,
        }));
        setEmptyChatHistoryBySession((previousState) => ({
          ...previousState,
          [sessionId]: true,
        }));
        handleChatSessionReady(sessionId);
        return sessionId;
      } catch (error) {
        console.error('Failed to create chat session from topics workspace', error);
        toast.error('We could not start your project. Please try again.');
        return null;
      } finally {
        setIsCreatingEmptyProject(false);
        creatingChatSessionPromiseRef.current = null;
      }
    })();

    creatingChatSessionPromiseRef.current = createPromise;
    return createPromise;
  }, [handleChatSessionReady, sessionState.chatSessionId]);

  const handleRunFlowTopicIdsChange = useCallback((nextTopicIds = [], promptAnswers = {}) => {
    runFlowPromptAnswersRef.current = promptAnswers;
    setRunFlowTopicIds((previousTopicIds) => {
      if (!Array.isArray(previousTopicIds)) return nextTopicIds;

      return areSameOrderedIds(previousTopicIds, nextTopicIds) ? previousTopicIds : nextTopicIds;
    });
  }, []);

  const handleProjectNameSync = useCallback((chatSessionId, name) => {
    if (!chatSessionId || typeof name !== 'string') return;

    setProjectNamesBySession((previousNames) => {
      if (previousNames?.[chatSessionId] === name) return previousNames;
      return { ...previousNames, [chatSessionId]: name };
    });
  }, []);

  const handleProjectFavoriteSync = useCallback((chatSessionId, isFavorite) => {
    if (!chatSessionId) return;

    setFavoriteStatusBySession((previousFavorites) => {
      if (previousFavorites?.[chatSessionId] === isFavorite) return previousFavorites;
      return { ...previousFavorites, [chatSessionId]: isFavorite };
    });
  }, []);

  const handleProjectNarrativeCreated = useCallback(({ chatSessionId, flowId, name }) => {
    if (!chatSessionId || !flowId) return;

    setOptimisticNarrativesBySession((previousNarratives) => {
      const sessionNarratives = previousNarratives?.[chatSessionId] || [];
      if (sessionNarratives.some((narrative) => Number(narrative.flowId) === Number(flowId))) {
        return previousNarratives;
      }

      return {
        ...previousNarratives,
        [chatSessionId]: [
          ...sessionNarratives,
          {
            createdAt: new Date().toISOString(),
            flowId: Number(flowId),
            isDeleted: false,
            name: name || 'Untitled Narrative',
            status: 'RUNNING',
          },
        ],
      };
    });

    setDeletedNarrativeIdsBySession((previousDeletedIds) => ({
      ...previousDeletedIds,
      [chatSessionId]: (previousDeletedIds?.[chatSessionId] || []).filter(
        (deletedId) => Number(deletedId) !== Number(flowId)
      ),
    }));

    setProjectNarrativesBySession((previousNarrativesBySession) => {
      const existingNarratives = Array.isArray(previousNarrativesBySession?.[chatSessionId])
        ? previousNarrativesBySession[chatSessionId]
        : [];

      const flowIdAsNumber = Number(flowId);
      const nextNarrative = {
        createdDatetime: null,
        id: flowIdAsNumber,
        isOwner: true,
        name: name || 'Untitled Narrative',
        status: 'RUNNING',
      };

      const existingIndex = existingNarratives.findIndex(
        (narrative) => Number(narrative?.id) === flowIdAsNumber
      );

      let nextNarratives;
      if (existingIndex > -1) {
        nextNarratives = [...existingNarratives];
        nextNarratives[existingIndex] = {
          ...nextNarratives[existingIndex],
          ...nextNarrative,
        };
      } else {
        nextNarratives = [nextNarrative, ...existingNarratives];
      }

      return {
        ...previousNarrativesBySession,
        [chatSessionId]: nextNarratives,
      };
    });
  }, []);

  const clearProjectDraftState = useCallback(
    ({ chatSessionId }) => {
      if (!chatSessionId || sessionState.chatSessionId !== chatSessionId) return;
      projectDraftRequestIdRef.current += 1;
      lastActiveDraftFlowStateRef.current = null;
      setProjectDraftNarrative(null);
      setProjectDraftLookup({ chatSessionId, status: 'loaded' });
    },
    [sessionState.chatSessionId]
  );

  const handleProjectDraftConsumed = useCallback(
    ({ chatSessionId }) => clearProjectDraftState({ chatSessionId }),
    [clearProjectDraftState]
  );

  const handleProjectDraftSaved = useCallback(
    ({ chatSessionId }) => refreshProjectDraft(chatSessionId),
    [refreshProjectDraft]
  );

  const handleProjectNarrativeDeleted = useCallback(
    ({ chatSessionId, flowId: deletedFlowId, shouldClearCanvasState, shouldClearRunFlowState }) => {
      if (!chatSessionId || deletedFlowId == null) return;

      setDeletedNarrativeIdsBySession((previousDeletedIds) => ({
        ...previousDeletedIds,
        [chatSessionId]: Array.from(
          new Set([...(previousDeletedIds?.[chatSessionId] || []), Number(deletedFlowId)])
        ),
      }));
      setProjectNarrativesBySession((previousNarrativesBySession) => ({
        ...previousNarrativesBySession,
        [chatSessionId]: (previousNarrativesBySession?.[chatSessionId] || []).filter(
          (narrative) => Number(narrative?.id) !== Number(deletedFlowId)
        ),
      }));
      setOptimisticNarrativesBySession((previousNarrativesBySession) => ({
        ...previousNarrativesBySession,
        [chatSessionId]: (previousNarrativesBySession?.[chatSessionId] || []).map((narrative) =>
          Number(narrative?.flowId) === Number(deletedFlowId)
            ? { ...narrative, isDeleted: true }
            : narrative
        ),
      }));
      setNarrativeNamesById((previousNamesById) => {
        if (!Object.prototype.hasOwnProperty.call(previousNamesById, deletedFlowId)) {
          return previousNamesById;
        }
        const nextNamesById = { ...previousNamesById };
        delete nextNamesById[deletedFlowId];
        return nextNamesById;
      });

      if (
        sessionState.chatSessionId === chatSessionId &&
        (shouldClearCanvasState || shouldClearRunFlowState)
      ) {
        setSynchronizedDraftFlowState(null);
        setActiveTopicConfiguration(null);
        setSelectedTopicCards([]);
      }
    },
    [sessionState.chatSessionId, setSynchronizedDraftFlowState]
  );

  const handleProjectNarrativeRenamed = useCallback(
    ({ chatSessionId, flowId: renamedFlowId, name }) => {
      if (!chatSessionId || !renamedFlowId || typeof name !== 'string') return;
      setNarrativeNamesById((previousNamesById) => {
        if (previousNamesById?.[renamedFlowId] === name) return previousNamesById;
        return { ...previousNamesById, [renamedFlowId]: name };
      });

      setProjectNarrativesBySession((previousNarrativesBySession) => {
        const sessionNarratives = Array.isArray(previousNarrativesBySession?.[chatSessionId])
          ? previousNarrativesBySession[chatSessionId]
          : [];

        let didChange = false;
        const nextSessionNarratives = sessionNarratives.map((narrative) => {
          if (Number(narrative?.id) !== Number(renamedFlowId)) return narrative;
          if (narrative?.name === name) return narrative;
          didChange = true;
          return { ...narrative, name };
        });

        if (!didChange) return previousNarrativesBySession;

        return {
          ...previousNarrativesBySession,
          [chatSessionId]: nextSessionNarratives,
        };
      });
      setOptimisticNarrativesBySession((previousNarrativesBySession) => ({
        ...previousNarrativesBySession,
        [chatSessionId]: (previousNarrativesBySession?.[chatSessionId] || []).map((narrative) =>
          Number(narrative?.flowId) === Number(renamedFlowId) ? { ...narrative, name } : narrative
        ),
      }));
    },
    []
  );

  const handleProjectDraftCleared = clearProjectDraftState;

  const handleProjectDraftDeleted = useCallback(
    (chatSessionId) => {
      if (!chatSessionId || sessionState.chatSessionId !== chatSessionId) return;

      projectDraftRequestIdRef.current += 1;
      lastActiveDraftFlowStateRef.current = null;
      setProjectDraftNarrative(null);
      setProjectDraftLookup({ chatSessionId, status: 'loaded' });
      setSynchronizedDraftFlowState(null);
      if (flowId == null) {
        setActiveTopicConfiguration(null);
        setSelectedTopicCards([]);
      }
    },
    [flowId, sessionState.chatSessionId]
  );

  useEffect(() => {
    if (!Array.isArray(websocketFlows) || websocketFlows.length === 0) return;
    const websocketFlowsById = new Map(
      websocketFlows.map((flow) => [Number(flow?.id), flow]).filter(([id]) => !Number.isNaN(id))
    );

    setProjectNarrativesBySession((previousNarrativesBySession) => {
      const nextNarrativesBySession = {};
      let didChange = false;

      Object.entries(previousNarrativesBySession).forEach(([chatSessionId, narratives]) => {
        if (!Array.isArray(narratives) || narratives.length === 0) {
          nextNarrativesBySession[chatSessionId] = narratives;
          return;
        }

        const nextNarratives = narratives.map((narrative) => {
          const matchedFlow = websocketFlowsById.get(Number(narrative?.id));
          if (!matchedFlow) return narrative;

          const nextStatus = matchedFlow?.status || narrative?.status;
          const nextNarrative = {
            ...narrative,
            createdDatetime: matchedFlow?.createdDatetime || narrative?.createdDatetime || null,
            name: matchedFlow?.name || narrative?.name || 'Untitled Narrative',
            status: nextStatus,
          };

          const hasChanged =
            nextNarrative.createdDatetime !== narrative?.createdDatetime ||
            nextNarrative.name !== narrative?.name ||
            nextNarrative.status !== narrative?.status;

          if (hasChanged) {
            didChange = true;
          }

          return hasChanged ? nextNarrative : narrative;
        });

        nextNarrativesBySession[chatSessionId] = nextNarratives;
      });

      return didChange ? nextNarrativesBySession : previousNarrativesBySession;
    });
  }, [websocketFlows]);

  const breakAwayToDraftFlowState = useCallback(
    async ({ topic }) => {
      if (!topic?.id) return false;

      const isViewingRun = flowId != null;
      const previousFlowState = isViewingRun ? null : draftFlowState;
      const inheritedPromptAnswers = isViewingRun
        ? runFlowPromptAnswersRef.current
        : (previousFlowState?.editablePromptAnswers ?? null);

      const chatSessionId = await ensureChatSession();
      if (!chatSessionId) return false;

      let baseTopicIds = [];
      if (Array.isArray(runFlowTopicIds)) {
        baseTopicIds = runFlowTopicIds;
      } else if (Array.isArray(activeRunFlow?.topics)) {
        baseTopicIds = activeRunFlow.topics
          .filter((topicItem) => !topicItem?.isHidden)
          .map((topicItem) => getRunTopicComponentId(topicItem))
          .filter((componentId) => componentId != null);
      } else if (Array.isArray(draftFlowState?.topics)) {
        baseTopicIds = draftFlowState.topics;
      }
      const nextComponentIds = mergeTopicIds(baseTopicIds, [topic.id]);
      const previousFlowId = flowId;
      const previousSelectedTopicCards = selectedTopicCards;
      const optimisticFlowState = previousFlowState
        ? { ...previousFlowState, focusFlowId: null, topics: nextComponentIds }
        : {
            editablePromptAnswers: inheritedPromptAnswers,
            focusFlowId: null,
            topics: nextComponentIds,
          };
      const requestId = ++breakawayAddRequestIdRef.current;

      setSynchronizedDraftFlowState(optimisticFlowState);
      setActiveTopicConfiguration(null);
      clearRunSelection({ chatSessionId });
      setSelectedTopicCards([]);
      setIsTemplateDirty(true);

      try {
        await clearFocusedRunFlow({ chatSessionId, previousFlowId });

        const project = await readProject({ chatSessionId });
        if (!project?.id) throw new Error('Project ID is required to update a Draft.');

        const updatedDraft = await updateNarrativeDraft({
          draft: optimisticFlowState,
          projectId: project.id,
        });
        trackTopicCardAddRemove({
          action: 'add',
          chatSessionId,
          environmentState: 'draft',
          sourceOfChange: 'manual',
          topicId: topic.id,
          topicName: topic.name,
        });
        const updatedFlowState = await hydrateDynamicNarrativeFlowState({
          flowState: mergeDraftIntoFlowState({
            draft: updatedDraft,
            flowState: optimisticFlowState,
          }),
        });

        if (requestId !== breakawayAddRequestIdRef.current) return false;
        setSynchronizedDraftFlowState(updatedFlowState);
        refreshProjectDraft(chatSessionId);
        return true;
      } catch (error) {
        if (requestId !== breakawayAddRequestIdRef.current) return false;
        console.error('Failed to create draft flow state from run Narrative', error);
        await restoreFocusedRunFlow({ chatSessionId, previousFlowId });
        setSynchronizedDraftFlowState(previousFlowState);
        setActiveTopicConfiguration(null);
        restoreRunSelection(previousFlowId, { chatSessionId });
        setSelectedTopicCards(previousSelectedTopicCards);
        toast.error('We could not add that topic card. Please try again.');
        return false;
      }
    },
    [
      activeRunFlow,
      clearFocusedRunFlow,
      clearRunSelection,
      draftFlowState,
      ensureChatSession,
      flowId,
      restoreFocusedRunFlow,
      restoreRunSelection,
      refreshProjectDraft,
      runFlowTopicIds,
      selectedTopicCards,
    ]
  );

  const requestInsertTopicCardBreakaway = useCallback(({ topic }) => {
    if (!topic?.id) return Promise.resolve(false);

    if (pendingInsertTopicCardResolverRef.current) {
      pendingInsertTopicCardResolverRef.current(false);
    }

    setIsInsertingTopicCard(false);
    return new Promise((resolve) => {
      pendingInsertTopicCardResolverRef.current = resolve;
      setPendingInsertTopicCard({ topic });
    });
  }, []);

  const closePendingInsertTopicCard = useCallback((result = false) => {
    setIsInsertingTopicCard(false);
    const resolver = pendingInsertTopicCardResolverRef.current;
    pendingInsertTopicCardResolverRef.current = null;
    setPendingInsertTopicCard(null);
    resolver?.(result);
  }, []);

  const confirmPendingInsertTopicCard = useCallback(async () => {
    if (isInsertingTopicCard) return;

    if (!pendingInsertTopicCard?.topic) {
      closePendingInsertTopicCard(false);
      return;
    }

    setIsInsertingTopicCard(true);
    const wasInserted = await breakAwayToDraftFlowState({
      topic: pendingInsertTopicCard.topic,
    });
    closePendingInsertTopicCard(wasInserted);
  }, [
    breakAwayToDraftFlowState,
    closePendingInsertTopicCard,
    isInsertingTopicCard,
    pendingInsertTopicCard,
  ]);

  const breakAwayToDraftFlowStateWithReplacement = useCallback(
    async ({ nextTopic, previousTopicId }) => {
      if (!nextTopic?.id || previousTopicId == null) return false;

      const isViewingRun = flowId != null;
      const previousFlowState = isViewingRun ? null : draftFlowState;
      const inheritedPromptAnswers = isViewingRun
        ? runFlowPromptAnswersRef.current
        : (previousFlowState?.editablePromptAnswers ?? null);

      const chatSessionId = await ensureChatSession();
      if (!chatSessionId) return false;

      let baseTopicIds = [];
      if (Array.isArray(runFlowTopicIds)) {
        baseTopicIds = runFlowTopicIds;
      } else if (Array.isArray(activeRunFlow?.topics)) {
        baseTopicIds = activeRunFlow.topics
          .filter((topicItem) => !topicItem?.isHidden)
          .map((topicItem) => getRunTopicComponentId(topicItem))
          .filter((componentId) => componentId != null);
      } else if (Array.isArray(draftFlowState?.topics)) {
        baseTopicIds = draftFlowState.topics;
      }

      const nextComponentIds = baseTopicIds.map((topicId) =>
        String(topicId) === String(previousTopicId) ? nextTopic.id : topicId
      );
      const previousFlowId = flowId;
      const previousSelectedTopicCards = selectedTopicCards;
      const optimisticFlowState = previousFlowState
        ? { ...previousFlowState, focusFlowId: null, topics: nextComponentIds }
        : {
            editablePromptAnswers: inheritedPromptAnswers,
            focusFlowId: null,
            topics: nextComponentIds,
          };
      const requestId = ++breakawayAddRequestIdRef.current;

      setSynchronizedDraftFlowState(optimisticFlowState);
      setActiveTopicConfiguration(null);
      clearRunSelection({ chatSessionId });
      setSelectedTopicCards([]);
      setIsTemplateDirty(true);

      try {
        await clearFocusedRunFlow({ chatSessionId, previousFlowId });

        const project = await readProject({ chatSessionId });
        if (!project?.id) throw new Error('Project ID is required to update a Draft.');

        const updatedDraft = await updateNarrativeDraft({
          draft: optimisticFlowState,
          projectId: project.id,
        });
        const updatedFlowState = await hydrateDynamicNarrativeFlowState({
          flowState: mergeDraftIntoFlowState({
            draft: updatedDraft,
            flowState: optimisticFlowState,
          }),
        });

        if (requestId !== breakawayAddRequestIdRef.current) return false;
        setSynchronizedDraftFlowState(updatedFlowState);
        refreshProjectDraft(chatSessionId);
        return true;
      } catch (error) {
        if (requestId !== breakawayAddRequestIdRef.current) return false;
        console.error('Failed to replace run topic card in draft Narrative', error);
        await restoreFocusedRunFlow({ chatSessionId, previousFlowId });
        setSynchronizedDraftFlowState(previousFlowState);
        setActiveTopicConfiguration(null);
        restoreRunSelection(previousFlowId, { chatSessionId });
        setSelectedTopicCards(previousSelectedTopicCards);
        toast.error('We could not update that topic card. Please try again.');
        return false;
      }
    },
    [
      activeRunFlow,
      clearFocusedRunFlow,
      clearRunSelection,
      draftFlowState,
      ensureChatSession,
      flowId,
      restoreFocusedRunFlow,
      restoreRunSelection,
      refreshProjectDraft,
      runFlowTopicIds,
      selectedTopicCards,
    ]
  );

  const handleInsertTopicCard = useCallback(
    async (group) => {
      if (!group?.grouping || !Array.isArray(group?.topics) || group.topics.length === 0) return;
      const addedIdSet = new Set(topicIdsForLibrary.filter((id) => id != null).map(String));
      if (checkIsTopicCardCountMax(addedIdSet.size)) return;
      const totalVariants = group.topics.length;
      const availableTopics = group.topics.filter((topic) => !addedIdSet.has(String(topic?.id)));
      if (!availableTopics.length) return;

      if (totalVariants === 1 && !availableTopics[0]?.variant) {
        const topic = availableTopics[0];
        if (flowId) {
          await requestInsertTopicCardBreakaway({ topic });
          return;
        }

        setSelectedTopicCards((prev) => {
          if (prev.some((item) => String(item.id) === String(topic.id))) return prev;
          return [...prev, { id: topic.id, name: topic.name, vizType: topic.vizType }];
        });
        setIsTemplateDirty(true);
        void (async () => {
          const chatSessionId = sessionState.chatSessionId || (await ensureChatSession());
          if (!chatSessionId) return;
          trackTopicCardAddRemove({
            action: 'add',
            chatSessionId,
            environmentState: 'draft',
            sourceOfChange: 'manual',
            topicId: topic.id,
            topicName: topic?.name ?? 'n/a',
          });
        })();
        return;
      }
      const config = buildTopicConfiguration(group);
      if (!config) return;
      const selectedVariant = findFirstAvailableVariantSelection({
        config,
        occupiedTopicIdSet: addedIdSet,
      });

      setActiveTopicConfiguration({
        config,
        id: `config:${group.grouping}`,
        mode: flowId ? 'run' : 'draft',
        name: group.grouping,
        occupiedTopicIds: Array.from(addedIdSet),
        previousTopicId: null,
        selectedVariant,
        vizType: group.topics[0]?.vizType,
      });
    },
    [
      ensureChatSession,
      flowId,
      requestInsertTopicCardBreakaway,
      sessionState.chatSessionId,
      topicIdsForLibrary,
    ]
  );

  const handleInsertRecommendedTopicGroups = useCallback(
    async (groups = []) => {
      for (const group of groups) {
        const topic = group?.topics?.[0];
        if (!topic?.id) continue;

        await handleInsertTopicCard({
          ...group,
          topics: [{ id: topic.id, name: topic.name, vizType: topic.vizType }],
        });
      }
    },
    [handleInsertTopicCard]
  );

  const confirmRunTopicDeletion = useCallback(async () => {
    if (!runTopicPendingDeletion?.runTopicId) return;

    setIsDeletingRunTopic(true);
    try {
      await softDeleteFlowTopic({ topicId: runTopicPendingDeletion.runTopicId });
      let runTopics = Array.isArray(activeRunFlow?.topics) ? activeRunFlow.topics : [];
      if (!runTopics.length && flowId) {
        try {
          const runFlowResponse = await readFlow({ id: flowId });
          runTopics = Array.isArray(runFlowResponse?.topics) ? runFlowResponse.topics : [];
        } catch (error) {
          console.error('Failed to load run topics for topic sync after removal', error);
        }
      }

      const nextComponentIdsFromRun = runTopics
        .filter((topic) => String(topic?.id) !== String(runTopicPendingDeletion.runTopicId))
        .map((topic) => getRunTopicComponentId(topic))
        .filter((componentId) => componentId != null);
      const nextComponentIds =
        nextComponentIdsFromRun.length > 0
          ? nextComponentIdsFromRun
          : (Array.isArray(draftFlowState?.topics) ? draftFlowState.topics : []).filter(
              (topicId) => String(topicId) !== String(runTopicPendingDeletion.topicId)
            );

      const optimisticFlowState = draftFlowState
        ? { ...draftFlowState, focusFlowId: null, topics: nextComponentIds }
        : {
            focusFlowId: null,
            narrativeName: DEFAULT_DRAFT_NARRATIVE_NAME,
            topics: nextComponentIds,
          };
      setSynchronizedDraftFlowState(optimisticFlowState);

      if (sessionState.chatSessionId) {
        await updateFlowStateTopicIds({
          chatSessionId: sessionState.chatSessionId,
          topicIds: nextComponentIds,
        });
        const updatedFlowState = await reconcileDraftFlowState({
          chatSessionId: sessionState.chatSessionId,
          fallbackFlowState: optimisticFlowState,
        });

        setSynchronizedDraftFlowState(updatedFlowState);
      }

      setSelectedTopicCards((prev) =>
        prev.filter((item) => String(item?.id) !== String(runTopicPendingDeletion.topicId))
      );
      setRunFlowRefreshKey((prev) => prev + 1);
      setRunTopicPendingDeletion(null);
      setIsTemplateDirty(true);
    } catch (error) {
      console.error('Failed to remove run topic card from topics list', error);
      toast.error('We could not remove that topic card. Please try again.');
    } finally {
      setIsDeletingRunTopic(false);
    }
  }, [
    activeRunFlow,
    draftFlowState,
    flowId,
    reconcileDraftFlowState,
    runTopicPendingDeletion,
    sessionState.chatSessionId,
  ]);

  const handlePageChange = useCallback(
    (nextPage) => {
      if (
        nextPage === PAGES.CHAT &&
        hasChatAccess &&
        !hasActiveProjectContext &&
        activePage !== PAGES.LANDING
      ) {
        setActivePage(PAGES.LANDING);
        return;
      }

      if (nextPage === PAGES.TEMPLATES) {
        if (hasActiveProjectContext) {
          if (activePage === PAGES.WORKSPACE_TEMPLATES && !isLeftPaneCollapsed) {
            setIsLeftPaneCollapsed(true);
            return;
          }

          setActivePage(PAGES.WORKSPACE_TEMPLATES);
          setIsLeftPaneCollapsed(false);
          return;
        }

        setActivePage(PAGES.TEMPLATES);
        return;
      }

      if (nextPage === 'new-project') {
        requestDisposableProjectExit(() => {
          resetSessionState();
          setActivePage(defaultLandingPage);
        });
        return;
      }

      if (nextPage === PAGES.LANDING) {
        requestDisposableProjectExit(() => {
          resetSessionState();
          setActivePage(PAGES.LANDING);
        });
        return;
      }

      const nextShowsWorkspace = WORKSPACE_PAGES.has(nextPage);
      if (nextShowsWorkspace) {
        if (activePage === nextPage && !isLeftPaneCollapsed) {
          setIsLeftPaneCollapsed(true);
          return;
        }

        setActivePage(nextPage);
        setIsLeftPaneCollapsed(false);
        return;
      }

      if (shouldShowWorkspace) {
        resetSessionState();
      }

      setActivePage(nextPage);
    },
    [
      activePage,
      defaultLandingPage,
      hasActiveProjectContext,
      hasChatAccess,
      isLeftPaneCollapsed,
      requestDisposableProjectExit,
      resetSessionState,
      shouldShowWorkspace,
    ]
  );

  const handleInvalidUrlProject = useCallback(
    (projectSessionId) => {
      if (!projectSessionId || !chatSessionIdFromUrl) return false;
      if (String(projectSessionId) !== String(chatSessionIdFromUrl)) return false;

      clearActiveSessionSelection({
        chatSessionId: projectSessionId,
        shouldRemoveSessionScopedState: true,
      });
      setActivePage(defaultLandingPage);
      toast.error('The project does not exist, so we took you back to Narratives home.');
      return true;
    },
    [chatSessionIdFromUrl, clearActiveSessionSelection, defaultLandingPage]
  );

  const handleProjectHistoryRefreshHandled = useCallback(() => {
    setLastDeletedProjectSessionId(null);
  }, []);

  const handleChatMessageSent = useCallback(() => {
    if (sessionState.chatSessionId) {
      setEmptyChatHistoryBySession((previousState) => {
        if (!previousState?.[sessionState.chatSessionId]) return previousState;

        const nextState = { ...previousState };
        delete nextState[sessionState.chatSessionId];
        return nextState;
      });
    }
    setInitialInput('');
    loadTokenUsage();
  }, [loadTokenUsage, sessionState.chatSessionId]);

  const handleLandingSubmit = (input) => setInitialInput(input);
  const handleHelpClick = () => {
    window.open(HELP_CENTER_BASE_URL, '_blank', 'noopener,noreferrer');
  };

  const handleChatStateSnapshot = useCallback((snapshot) => {
    setChatSnapshot(snapshot);
    const chatSessionId = snapshot?.chatSessionId;
    if (!chatSessionId || !snapshot?.wasHydrated) return;

    const hasMessages = Array.isArray(snapshot?.messages) && snapshot.messages.length > 0;
    setEmptyChatHistoryBySession((previousState) => {
      if (!hasMessages) {
        if (previousState?.[chatSessionId]) return previousState;
        return {
          ...previousState,
          [chatSessionId]: true,
        };
      }

      if (!previousState?.[chatSessionId]) return previousState;

      const nextState = { ...previousState };
      delete nextState[chatSessionId];
      return nextState;
    });
  }, []);

  const handlePendingChatResponseChange = useCallback((chatSessionId, pendingResponse) => {
    setPendingChatResponse((previousPendingResponse) => {
      if (pendingResponse) return pendingResponse;
      return previousPendingResponse?.chatSessionId === chatSessionId
        ? null
        : previousPendingResponse;
    });
  }, []);

  useEffect(() => {
    document.body.classList.toggle('no-scroll', shouldDisableBodyScroll);

    return () => {
      document.body.classList.remove('no-scroll');
    };
  }, [shouldDisableBodyScroll]);

  useEffect(() => {
    if (!shouldLoadNarrativeTopicGroups) return;
    void loadNarrativeTopicGroups({ showError: !isChat });
  }, [isChat, loadNarrativeTopicGroups, shouldLoadNarrativeTopicGroups]);

  useEffect(() => {
    if (
      !sessionState.chatSessionId ||
      isChat ||
      hasWorkspaceContent ||
      hasRequestedInitialFlowSelection
    ) {
      if (!isChat && !initialRequestedFlowIdRef.current) {
        setIsResolvingInitialCanvasContent(false);
      }
      return;
    }

    const abortController = new AbortController();

    readFlowState({
      chatSessionId: sessionState.chatSessionId,
      signal: abortController.signal,
    })
      .then(({ flowId: nextFlowId, flowState: nextFlowState }) => {
        if (abortController.signal.aborted) return;

        handleFlowStateChange(nextFlowState || null);
        if (nextFlowId) {
          handleFlowSelected(nextFlowId, { source: 'flow-state-hydration' });
        }
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || error?.name === 'CanceledError') return;
        console.error('Failed to hydrate workspace flow state', error);
      })
      .finally(() => {
        if (!abortController.signal.aborted) setIsResolvingInitialCanvasContent(false);
      });

    return () => {
      abortController.abort();
    };
  }, [
    handleFlowSelected,
    handleFlowStateChange,
    hasWorkspaceContent,
    isChat,
    hasRequestedInitialFlowSelection,
    sessionState.chatSessionId,
  ]);

  useEffect(() => {
    if (
      !chatSessionIdFromUrl ||
      initialFlowIdFromNavigation == null ||
      narrativeIdFromUrl ||
      sessionState.chatSessionId !== chatSessionIdFromUrl
    ) {
      return;
    }

    const selectionKey = `${chatSessionIdFromUrl}:${initialFlowIdFromNavigation}`;
    if (previousRequestedFlowSelectionRef.current === selectionKey) return;

    previousRequestedFlowSelectionRef.current = selectionKey;
    void handleProjectNarrativeSelected({
      chatSessionId: chatSessionIdFromUrl,
      flowId: initialFlowIdFromNavigation,
    }).finally(() => setIsResolvingInitialCanvasContent(false));
  }, [
    chatSessionIdFromUrl,
    handleProjectNarrativeSelected,
    initialFlowIdFromNavigation,
    narrativeIdFromUrl,
    sessionState.chatSessionId,
  ]);

  useEffect(() => {
    if (selectedTagsKey) return;
    setVisibleTopicGroups(narrativeTopicGroups);
  }, [narrativeTopicGroups, selectedTagsKey]);

  useEffect(() => {
    if (!isTopics || !selectedTagsKey) {
      tagFilterRequestIdRef.current += 1;
      return;
    }

    const requestId = ++tagFilterRequestIdRef.current;
    const loadFilteredNarrativeTopicGroups = async () => {
      try {
        const topics = await readNarrativeTopics({
          signal: undefined,
          tags: selectedTagsKey.split(','),
        });
        const normalizedTopics = Array.isArray(topics) ? topics : [];
        if (tagFilterRequestIdRef.current !== requestId) {
          return;
        }
        setVisibleTopicGroups(normalizedTopics);
      } catch (error) {
        if (tagFilterRequestIdRef.current !== requestId) {
          return;
        }
        console.error('Error fetching filtered narrative topics:', error);
        toast.error('Error loading topics. Please refresh the page or try again.');
      }
    };

    void loadFilteredNarrativeTopicGroups();
  }, [isTopics, selectedTagsKey]);

  useEffect(() => {
    const previous = previousChatSessionIdFromUrlRef.current;
    previousChatSessionIdFromUrlRef.current = chatSessionIdFromUrl;

    if (!chatSessionIdFromUrl) {
      setIsValidatingUrlProject(false);
      setHasCompletedInitialUrlProjectValidation(true);
      isClearingChatSessionRef.current = false;
      if (!sessionState.chatSessionId) return;
      resetSessionState();
      setActivePage(defaultLandingPage);
      return;
    }

    if (isClearingChatSessionRef.current) {
      return;
    }

    const abortController = new AbortController();
    setIsValidatingUrlProject(true);

    const syncUrlProjectSession = async () => {
      try {
        const project = await readProject({
          chatSessionId: chatSessionIdFromUrl,
          signal: abortController.signal,
        });

        if (abortController.signal.aborted) return;
        if (typeof project?.name === 'string') {
          handleProjectNameSync(chatSessionIdFromUrl, project.name);
        }
        if (previous != null && previous !== chatSessionIdFromUrl) {
          resetCanvasSelectionState({ shouldResetTopicFilters: true });
          setFlowId(null);
        }
        setSessionState((prev) => {
          if (prev.chatSessionId === chatSessionIdFromUrl) return prev;
          return { ...prev, chatSessionId: chatSessionIdFromUrl };
        });
        setActivePage((prev) => (prev === PAGES.LANDING ? PAGES.CHAT : prev));
      } catch (error) {
        if (error?.name === 'AbortError' || error?.name === 'CanceledError') return;
        if (abortController.signal.aborted) return;

        const status = error?.response?.status;
        if (narrativeIdFromUrl && [403, 404].includes(status)) {
          navigate(`/dashboard/narrative/${narrativeIdFromUrl}`, { replace: true });
          return;
        }

        const isMissingProject = [404, 422].includes(status);
        const errorMessage =
          status === 403
            ? 'You do not have permission to view that page, so we took you back to the landing page.'
            : 'The project does not exist, so we took you back to Narratives home.';

        if (status === 403 || isMissingProject) {
          clearActiveSessionSelection({
            chatSessionId: chatSessionIdFromUrl,
            shouldRemoveSessionScopedState: true,
          });
          setActivePage(defaultLandingPage);
          toast.error(errorMessage);
          return;
        }

        if (previous != null && previous !== chatSessionIdFromUrl) {
          resetCanvasSelectionState({ shouldResetTopicFilters: true });
          setFlowId(null);
        }
        setSessionState((prev) => {
          if (prev.chatSessionId === chatSessionIdFromUrl) return prev;
          return { ...prev, chatSessionId: chatSessionIdFromUrl };
        });
        setActivePage((prev) => (prev === PAGES.LANDING ? PAGES.CHAT : prev));
      } finally {
        if (!abortController.signal.aborted) {
          setIsValidatingUrlProject(false);
          setHasCompletedInitialUrlProjectValidation(true);
        }
      }
    };

    void syncUrlProjectSession();

    return () => {
      abortController.abort();
    };
  }, [
    chatSessionIdFromUrl,
    defaultLandingPage,
    handleProjectNameSync,
    navigate,
    narrativeIdFromUrl,
    resetCanvasSelectionState,
    sessionState.chatSessionId,
  ]);

  useEffect(() => {
    if (
      !chatSessionIdFromUrl ||
      !narrativeIdFromUrl ||
      sessionState.chatSessionId !== chatSessionIdFromUrl ||
      isValidatingUrlProject ||
      String(flowId) === String(narrativeIdFromUrl)
    ) {
      return undefined;
    }

    const abortController = new AbortController();

    const validateUrlNarrative = async () => {
      try {
        const flows = await readProjectFlows({
          chatSessionId: chatSessionIdFromUrl,
          signal: abortController.signal,
        });
        if (abortController.signal.aborted) return;

        const requestedFlow = (Array.isArray(flows) ? flows : []).find(
          (flow) => String(flow?.id) === String(narrativeIdFromUrl)
        );

        if (!requestedFlow) {
          setFlowId(null);

          let restoredFlowId = null;

          try {
            const { flowId: savedFlowId, flowState: savedFlowState } = await readFlowState({
              chatSessionId: chatSessionIdFromUrl,
              signal: abortController.signal,
            });
            if (abortController.signal.aborted) return;

            handleFlowStateChange(savedFlowState || null);
            if (savedFlowId) {
              restoredFlowId = savedFlowId;
              handleFlowSelected(savedFlowId, { source: 'flow-state-hydration' });
            }
          } finally {
            if (!abortController.signal.aborted) {
              replaceCanvasQueryParams({
                chatSessionId: chatSessionIdFromUrl,
                narrativeId: restoredFlowId ?? null,
              });
            }
          }

          return;
        }

        if (requestedFlow.isOwner === false) {
          navigate(`/dashboard/narrative/${requestedFlow.id}`, { replace: true });
          return;
        }

        const wasSelected = await handleProjectNarrativeSelected({
          chatSessionId: chatSessionIdFromUrl,
          flowId: requestedFlow.id,
        });
        if (!abortController.signal.aborted && !wasSelected) {
          setIsResolvingInitialCanvasContent(false);
        }
      } catch (error) {
        if (error?.name === 'AbortError' || error?.name === 'CanceledError') return;
        if (abortController.signal.aborted) return;
        console.error('Failed to validate the Narrative requested in the canvas URL', error);
        toast.error('Unable to open this narrative. Please refresh and try again.');
        setIsResolvingInitialCanvasContent(false);
      }
    };

    void validateUrlNarrative();

    return () => {
      abortController.abort();
    };
  }, [
    chatSessionIdFromUrl,
    handleFlowSelected,
    handleFlowStateChange,
    handleProjectNarrativeSelected,
    isValidatingUrlProject,
    narrativeIdFromUrl,
    navigate,
    replaceCanvasQueryParams,
    resetCanvasSelectionState,
    flowId,
    sessionState.chatSessionId,
  ]);

  useEffect(() => {
    if (!chatSessionIdFromUrl) {
      setIsResolvingInitialCanvasContent(false);
      return;
    }
    // The bare-draft path (no requested flow at all) clears the flag itself once its own
    // fetch settles, so only the two "a specific flow was requested" paths are tracked here.
    if (initialNarrativeIdFromUrlRef.current && String(flowId) === String(narrativeIdFromUrl)) {
      setIsResolvingInitialCanvasContent(false);
      return;
    }
    if (
      initialFlowIdFromNavigationRef.current &&
      String(flowId) === String(initialFlowIdFromNavigationRef.current)
    ) {
      setIsResolvingInitialCanvasContent(false);
    }
  }, [chatSessionIdFromUrl, flowId, narrativeIdFromUrl]);

  useEffect(() => {
    if (shouldShowWorkspace) return;
    setFlowId(null);
    setSynchronizedDraftFlowState(null);
  }, [shouldShowWorkspace]);

  useEffect(() => {
    if (!hasSelectedTopics || sessionState.chatSessionId) return;
    ensureChatSession();
  }, [ensureChatSession, hasSelectedTopics, sessionState.chatSessionId]);

  const loadNarrativeTopicTags = useCallback(() => {
    if (hasLoadedTopicTags || narrativeTopicTagsRequestRef.current) return () => {};

    const abortController = new AbortController();
    const request = (async () => {
      try {
        const tags = await readNarrativeTopicsTags({ signal: abortController.signal });
        if (abortController.signal.aborted) return;
        setHasTagLoadError(false);
        setAvailableTags(Array.isArray(tags) ? tags : []);
        setHasLoadedTopicTags(true);
      } catch (error) {
        if (abortController.signal.aborted) return;
        console.error('Failed to load narrative topic tags', error);
        setHasTagLoadError(true);
        setAvailableTags([]);
      } finally {
        narrativeTopicTagsRequestRef.current = null;
      }
    })();

    narrativeTopicTagsRequestRef.current = request;

    return () => {
      abortController.abort();
      narrativeTopicTagsRequestRef.current = null;
    };
  }, [hasLoadedTopicTags]);

  useEffect(() => {
    if (!isTopics) return undefined;

    return loadNarrativeTopicTags();
  }, [isTopics, loadNarrativeTopicTags]);

  const handleCloseToolWindow = useCallback(() => {
    setIsLeftPaneCollapsed(true);
  }, []);

  const handleTemplateSaved = useCallback(() => {
    setCustomTemplatesRefreshKey((previousKey) => previousKey + 1);
  }, []);

  const handleTemplateSelected = useCallback(
    async ({ activeTab = 'numerator', source = 'workspace', template }) => {
      const topicIds = getTemplateTopicIds(template);
      const optimisticFlowState = {
        editablePromptAnswers: {},
        narrativeName: template?.name || DEFAULT_DRAFT_NARRATIVE_NAME,
        topics: topicIds,
      };
      const rollbackState = {
        chatSessionId: sessionState.chatSessionId,
        draftFlowState,
        flowId,
        isTemplateDirty,
        selectedTopicCards,
      };

      let chatSessionId = sessionState.chatSessionId;
      let requestId = null;

      try {
        const shouldCreateNewProject = source === 'full-page' && !shouldShowWorkspace;
        if (shouldCreateNewProject) {
          resetSessionState();
        }

        requestId = ++templateApplyRequestIdRef.current;

        if (!chatSessionId) {
          chatSessionId = await ensureChatSession();
        }

        if (requestId !== templateApplyRequestIdRef.current) return;
        if (!chatSessionId) return;

        setWorkspaceTemplateContext({
          activeTab: activeTab === 'custom' ? 'custom' : 'numerator',
          selectedTemplateId: template?.id ?? null,
        });
        handleChatSessionReady(chatSessionId);
      } catch (error) {
        if (requestId !== templateApplyRequestIdRef.current) return;
        console.error('Failed to create chat session from template selection', error);
        toast.error('We could not start a new project. Please try again.');
        return;
      }
      setSynchronizedDraftFlowState(optimisticFlowState);
      clearRunSelection({ chatSessionId });
      setSelectedTopicCards([]);
      setActivePage(PAGES.WORKSPACE_TEMPLATES);
      setIsLeftPaneCollapsed(false);
      setPendingTemplateSelection(null);

      const didKeepSession = rollbackState.chatSessionId === chatSessionId;
      const previousFocusedFlowId = didKeepSession ? rollbackState.flowId : null;

      try {
        await clearFocusedRunFlow({ chatSessionId, previousFlowId: previousFocusedFlowId });

        const project = await readProject({ chatSessionId });
        if (!project?.id) throw new Error('Project ID is required to update a Draft.');

        const updatedDraft = await updateNarrativeDraft({
          draft: optimisticFlowState,
          projectId: project.id,
        });
        const updatedFlowState = await hydrateDynamicNarrativeFlowState({
          flowState: mergeDraftIntoFlowState({
            draft: updatedDraft,
            flowState: optimisticFlowState,
          }),
        });

        if (requestId !== templateApplyRequestIdRef.current) return;
        setSynchronizedDraftFlowState({
          ...(updatedFlowState || {}),
          narrativeName:
            updatedFlowState?.narrativeName ||
            optimisticFlowState?.narrativeName ||
            DEFAULT_DRAFT_NARRATIVE_NAME,
        });
        refreshProjectDraft(chatSessionId);
        setIsTemplateDirty(false);
        trackNarrativeTemplateUsed({
          activeTab,
          projectId: project.id,
          source,
          template,
        });
      } catch (error) {
        if (requestId !== templateApplyRequestIdRef.current) return;
        console.error('Failed to hydrate draft flow state from template selection', error);
        toast.error('We could not load that template. Please try again.');
        // Only restore the previous run when the template application stayed in the same session.
        // A project created for this template must not inherit the previous project's run.
        await restoreFocusedRunFlow({ chatSessionId, previousFlowId: previousFocusedFlowId });
        restoreRunSelection(previousFocusedFlowId, { chatSessionId });
        setSynchronizedDraftFlowState(rollbackState.draftFlowState);
        setIsTemplateDirty(rollbackState.isTemplateDirty);
        setSelectedTopicCards(rollbackState.selectedTopicCards);
      }
    },
    [
      clearFocusedRunFlow,
      clearRunSelection,
      draftFlowState,
      ensureChatSession,
      handleChatSessionReady,
      isTemplateDirty,
      refreshProjectDraft,
      resetSessionState,
      restoreFocusedRunFlow,
      restoreRunSelection,
      selectedTopicCards,
      sessionState.chatSessionId,
      shouldShowWorkspace,
      flowId,
    ]
  );

  const requestTemplateSelection = useCallback(
    async (selection) => {
      if (!selection?.template) return;
      if (flowId != null || (hasCanvasCards && isTemplateDirty)) {
        setPendingTemplateSelection(selection);
        return;
      }
      await handleTemplateSelected(selection);
    },
    [flowId, handleTemplateSelected, hasCanvasCards, isTemplateDirty]
  );

  const isCapacityExhausted = tokenUsage?.tokensPercentRemaining <= 0;
  const capacityTrackerProps = tokenUsage
    ? {
        currentEndDatetime: tokenUsage.currentEndDatetime,
        nextStartDatetime: tokenUsage.nextStartDatetime,
        tokensPercentRemaining: tokenUsage.tokensPercentRemaining,
      }
    : {};
  const landingCapacityTracker = (
    <NexaCapacityTracker {...capacityTrackerProps} placement="bottom" />
  );
  const chatCapacityTracker = <NexaCapacityTracker {...capacityTrackerProps} />;

  return (
    <>
      <Flex className={styles['narratives-root']} gap="none">
        {shouldRenderNavigation && (
          <NarrativesNavigation
            activePage={activePage}
            hasChatAccess={hasChatAccess}
            isCollapsed={isLeftPaneCollapsed}
            onClick={handlePageChange}
          />
        )}

        {isLanding && (
          <>
            <NarrativesChatLandingPage
              capacityTracker={landingCapacityTracker}
              isCapacityExhausted={isCapacityExhausted}
              onNavigation={handlePageChange}
              onSubmit={handleLandingSubmit}
            />
            <IconButtonV2
              aria-label="Help"
              className={styles['help-button']}
              icon={<HelpV2Icon />}
              onClick={handleHelpClick}
              tooltip="Help"
              tooltipProps={{ placement: 'left' }}
              variant="subtle"
            />
          </>
        )}

        {isTemplates && (
          <>
            <Narratives
              hasTemplateAccess
              hideHelpButton
              onBuildYourOwnClick={() => handlePageChange(PAGES.TOPICS)}
              onTemplateSelect={(selection) =>
                requestTemplateSelection({
                  ...selection,
                  source: 'full-page',
                })
              }
            />
            <IconButtonV2
              aria-label="Help"
              className={styles['help-button']}
              icon={<HelpV2Icon />}
              onClick={handleHelpClick}
              tooltip="Help"
              tooltipProps={{ placement: 'left' }}
              variant="subtle"
            />
          </>
        )}

        {shouldRenderWorkspace && (
          <div className={styles['narratives-chat-shell']}>
            <div className={styles['narratives-chat-content']}>
              <div
                aria-hidden={isLeftPaneCollapsed}
                className={styles['narratives-chat-window']}
                data-collapsed={isLeftPaneCollapsed}
                data-testid="narratives-chat-window"
                inert={isLeftPaneCollapsed ? '' : undefined}
              >
                {isChat && (
                  <NarrativesChatWindow
                    activeFlowId={flowId}
                    allowCanvasHydrationOnMount={shouldAllowCanvasHydrationOnMount}
                    capacityTracker={chatCapacityTracker}
                    chatSessionId={sessionState.chatSessionId}
                    initialErrorMessage={activeChatSnapshot?.errorMessage}
                    initialInput={initialInput}
                    initialInputValue={activeChatSnapshot?.inputValue}
                    initialMessages={activeChatSnapshot?.messages}
                    isCapacityExhausted={isCapacityExhausted}
                    key={sessionState.chatSessionId || 'draft'}
                    liveFlows={websocketFlows}
                    narrativeNamesById={narrativeNamesById}
                    onChatSessionReady={handleChatSessionReady}
                    onCloseToolWindow={handleCloseToolWindow}
                    onFlowSelected={handleFlowSelected}
                    onFlowStateChange={handleFlowStateChange}
                    onInitializingChange={handleChatWindowInitializingChange}
                    onMessageSent={handleChatMessageSent}
                    onNarrativeDeleted={handleProjectNarrativeDeleted}
                    onNarrativeNameChange={handleProjectNarrativeRenamed}
                    onPendingResponseChange={handlePendingChatResponseChange}
                    onStateSnapshot={handleChatStateSnapshot}
                    optimisticNarratives={
                      optimisticNarrativesBySession?.[sessionState.chatSessionId]
                    }
                    pendingResponse={
                      pendingChatResponse?.chatSessionId === sessionState.chatSessionId
                        ? pendingChatResponse
                        : null
                    }
                    shouldBreakAwayFromRunOnMessage={Boolean(flowId)}
                    shouldHydrateOnMount={!activeChatSnapshot?.wasHydrated}
                    syncCanvasOnMount={!activeChatSnapshot?.wasHydrated}
                  />
                )}
                {isTopics && (
                  <NarrativesTopicsWindow
                    activeTopicIds={topicIdsForLibrary}
                    availableTags={availableTags}
                    hasTagLoadError={hasTagLoadError}
                    narrativeTopicGroups={visibleTopicGroups}
                    onCloseToolWindow={handleCloseToolWindow}
                    onInsertTopicCard={handleInsertTopicCard}
                    selectedTags={selectedTags}
                    setSelectedTags={setSelectedTags}
                  />
                )}
                {isWorkspaceTemplates && (
                  <NarrativesTemplatesWindow
                    customTemplatesRefreshKey={customTemplatesRefreshKey}
                    initialActiveTab={workspaceTemplateContext.activeTab}
                    initialActiveTemplateId={workspaceTemplateContext.selectedTemplateId}
                    onCloseToolWindow={handleCloseToolWindow}
                    onSelectTemplate={(selection) =>
                      requestTemplateSelection({
                        ...selection,
                        source: 'workspace',
                      })
                    }
                  />
                )}
                {isPastProjects && (
                  <NarrativesProjectHistory
                    activeDraftFlowState={draftFlowState}
                    activeFlowId={flowId}
                    activeSessionId={sessionState.chatSessionId}
                    deletedNarrativeIdsBySession={deletedNarrativeIdsBySession}
                    favoriteStatusBySession={favoriteStatusBySession}
                    handleChatSessionReady={handleChatSessionReady}
                    handleFlowSelected={handleFlowSelected}
                    isWindowCollapsed={isLeftPaneCollapsed}
                    lastDeletedProjectSessionId={lastDeletedProjectSessionId}
                    narrativeNamesById={narrativeNamesById}
                    onBeforeProjectOpen={handleBeforeProjectOpen}
                    onCloseToolWindow={handleCloseToolWindow}
                    onDraftDeleted={handleProjectDraftDeleted}
                    onDraftSelected={handleProjectDraftSelected}
                    onFavoriteChange={handleProjectFavoriteSync}
                    onNarrativeDeleted={handleProjectNarrativeDeleted}
                    onNarrativeNameChange={handleProjectNarrativeRenamed}
                    onNarrativeSelected={handleProjectNarrativeSelected}
                    onProjectHistoryRefreshHandled={handleProjectHistoryRefreshHandled}
                    onProjectNameChange={handleProjectNameSync}
                    onProjectNotFound={handleInvalidUrlProject}
                    pendingSelection={pendingProjectSelection}
                    projectDraftNarrative={projectDraftNarrative}
                    projectNamesBySession={projectNamesBySession}
                    projectNarrativesBySession={projectNarrativesBySession}
                  />
                )}
              </div>

              <div
                className={styles['narratives-chat-canvas']}
                data-collapsed={isLeftPaneCollapsed}
                data-testid="narratives-chat-canvas"
              >
                <NarrativesCanvas
                  activeNarrativeName={flowId != null ? narrativeNamesById?.[flowId] || null : null}
                  activeTopicConfiguration={activeTopicConfiguration}
                  chatSessionId={sessionState.chatSessionId}
                  externalIsFavorite={favoriteStatusBySession?.[sessionState.chatSessionId] ?? null}
                  externalProjectName={projectNamesBySession?.[sessionState.chatSessionId]}
                  flowId={flowId}
                  flowState={draftFlowState}
                  hasProjectDraft={projectDraftNarrative != null}
                  isProjectDraftLookupPending={isProjectDraftLookupPending}
                  isResolvingInitialCanvasContent={isResolvingInitialCanvasContent}
                  isRunTopicMutationInProgress={isDeletingRunTopic}
                  isTopicLibraryOpen={isTopics}
                  onActiveTopicConfigurationChange={setActiveTopicConfiguration}
                  onBrowseAllTopics={() => handlePageChange(PAGES.TOPICS)}
                  onDraftCleared={handleProjectDraftCleared}
                  onDraftConsumed={handleProjectDraftConsumed}
                  onDraftSaved={handleProjectDraftSaved}
                  onEnsureChatSession={ensureChatSession}
                  onFavoriteChange={handleProjectFavoriteSync}
                  onFlowSelected={handleFlowSelected}
                  onFlowStateChange={handleFlowStateChange}
                  onInsertRecommendedTopicGroups={handleInsertRecommendedTopicGroups}
                  onInsertRunTopicConfiguration={requestInsertTopicCardBreakaway}
                  onNarrativeCreated={handleProjectNarrativeCreated}
                  onNarrativeDeleted={handleProjectNarrativeDeleted}
                  onNarrativeNameChange={handleProjectNarrativeRenamed}
                  onProjectNameChange={handleProjectNameSync}
                  onProjectNotFound={handleInvalidUrlProject}
                  onReplaceRunTopicConfiguration={breakAwayToDraftFlowStateWithReplacement}
                  onRequestTopicConfigurationLookup={loadNarrativeTopicGroups}
                  onRunFlowTopicIdsChange={handleRunFlowTopicIdsChange}
                  onSelectedTopicsChange={setSelectedTopicCards}
                  onTemplateSaved={handleTemplateSaved}
                  runFlowRefreshKey={runFlowRefreshKey}
                  selectedTopicCards={selectedTopicCards}
                  topicConfigurationLookup={topicConfigurationLookup}
                />
              </div>
            </div>
          </div>
        )}
      </Flex>
      <ConfirmActionModal
        cancelText="Keep Editing"
        confirmText="Delete & Continue"
        confirmVariant="error"
        isActive={isDisposableProjectExitConfirmationOpen}
        isCancelDisabled={isDeletingDisposableProject}
        isConfirmDisabled={isDeletingDisposableProject}
        message="Rename this project or run a narrative to keep it. Otherwise, it will be permanently deleted when you continue."
        onCancel={handleDisposableProjectExitCancel}
        onConfirm={handleDisposableProjectExitConfirm}
        title="Delete this project?"
      />
      <ConfirmActionModal
        confirmText="Remove"
        confirmVariant="error"
        isActive={Boolean(runTopicPendingDeletion)}
        isConfirmDisabled={isDeletingRunTopic}
        message="Are you sure you would like to remove this topic card? You'll lose its results."
        onCancel={() => setRunTopicPendingDeletion(null)}
        onConfirm={confirmRunTopicDeletion}
        title="Remove"
      />
      <ConfirmActionModal
        confirmText="Insert Topic Card"
        isActive={Boolean(pendingInsertTopicCard)}
        isCancelDisabled={isInsertingTopicCard}
        isConfirmDisabled={isInsertingTopicCard}
        message="This will add the topic card to the canvas and create a new draft narrative. If your project has an existing draft narrative, it will be deleted. Run narratives will not be affected."
        onCancel={() => {
          if (isInsertingTopicCard) return;
          closePendingInsertTopicCard(false);
        }}
        onConfirm={confirmPendingInsertTopicCard}
        title="Insert Topic Card?"
      />
      <ConfirmActionModal
        confirmText="Apply Template"
        isActive={Boolean(pendingTemplateSelection)}
        message={`This will replace the cards currently on your canvas with the cards from ${
          pendingTemplateSelection?.template?.name || 'this template'
        } and create a new draft narrative. If your project has an existing draft narrative, it will be deleted.`}
        onCancel={() => setPendingTemplateSelection(null)}
        onConfirm={() => handleTemplateSelected(pendingTemplateSelection)}
        title="Apply Template?"
      />
    </>
  );
};

NarrativesChat.propTypes = {
  hasChatAccess: PropTypes.bool,
};

export default NarrativesChat;
